import type { AxiosInstance } from "axios";
import axios from "axios";
import { errorResponse } from "./api-interface";

const BASE_URLS = {
  apiUrl: import.meta.env.VITE_API_URL,
  aiUrl: import.meta.env.VITE_AI_URL,
  fileuploadUrl: import.meta.env.VITE_UPLOAD_API_URL,
  platformUrl: import.meta.env.VITE_PLATFORM_URL,
  platformServiceUrl: import.meta.env.VITE_PLATFORM_SERVICE_URL,
  signalRUrl: import.meta.env.VITE_SIGNALR_URL,
  dfdApiUrl: import.meta.env.VITE_DFD_API,
};

export const getAxiosInstance = (
  baseUrlKey: keyof typeof BASE_URLS = "apiUrl",
  accessToken: string = "",
  totalParts?: string,
): AxiosInstance => {
  const baseURL = BASE_URLS[baseUrlKey];

  const token = accessToken || import.meta.env.VITE_SESSION_TOKEN;

  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const isPlatformRequest =
    baseUrlKey === "fileuploadUrl" ||
    baseUrlKey === "platformUrl" ||
    baseUrlKey === "platformServiceUrl";

  const isDfdRequest = baseUrlKey === "dfdApiUrl";

  if (isPlatformRequest) {
    if (totalParts) headers["x-total-parts"] = totalParts;
  } else if (isDfdRequest) {
    headers["X-API-KEY"] = import.meta.env.VITE_DFD_API_KEY;
    headers["content-type"] = "application/json";
  } else {
    headers["X-API-KEY"] = import.meta.env.VITE_API_KEY;
    headers["content-type"] = "application/json";
  }

  const instance = axios.create({
    baseURL,
    headers,
  });

  instance.interceptors.request.use(async (config) => {
    const url = config.url || "";

    if (url.startsWith("data:")) {
      throw new Error("Blocked unsafe data URL to prevent DoS vulnerability");
    }

    // Always fetch a fresh token from the host auth service before every API
    // call so that idle-expired tokens are transparently refreshed.
    try {
      // if (isPlatformRequest || isDfdRequest) {
        // const authService = await import("mosaic_host_app/authService");
        // const freshToken = await authService.acquireToken();
        // if (freshToken) {
        //   config.headers.Authorization = `Bearer ${freshToken}`;
        // }
        const { msalInstance } = await import("../helpers/msalConfig");
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length > 0) {
          const response = await msalInstance.acquireTokenSilent({
            scopes: [import.meta.env.VITE_AUTH_SCOPE],
            account: accounts[0],
            forceRefresh: false,
          });
          if (response?.accessToken) {
            config.headers.Authorization = `Bearer ${response.accessToken}`;
          }
        }
      // } else {
      //   // ContentQA APIs: refresh module token silently
      //   const { contentqaMsalInstance } =
      //     await import("../helpers/msalConfigModule");
      //   const accounts = contentqaMsalInstance.getAllAccounts();

      //   if (accounts.length > 0) {
      //     const response = await contentqaMsalInstance.acquireTokenSilent({
      //       scopes: [import.meta.env.VITE_CONTENTQA_SCOPE],
      //       account: accounts[0],
      //       forceRefresh: false,
      //     });

      //     if (response?.accessToken) {
      //       config.headers.Authorization = `Bearer ${response.accessToken}`;
      //     }
      //   }
      // }
    } catch {
      // retain the token already set during instance creation as fallback
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isAxiosError(error)) {
        const response = errorResponse(
          error.response?.data?.message ?? error.message,
          {
            status: error.response?.status,
            code: error.code,
            data: error.response?.data,
          },
        );

        return Promise.reject(new Error(JSON.stringify(response)));
      }

      const response = errorResponse("Unexpected error occurred");
      return Promise.reject(new Error(JSON.stringify(response)));
    },
  );

  return instance;
};
